import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { filter } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {

  private readonly ENDPOINT = `${environment.APIEndpoint}/meta-service/events`;
  private pageViewsBound = false;
  private initialPageViewTracked = false;

  private userData: {
    em?: string;
    ph?: string;
    fn?: string;
    ln?: string;
    [k: string]: any;
  } = {};

  constructor(
    private router: Router,
    private http: HttpClient,
    private authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private route: ActivatedRoute
  ) {}

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private getFbq(): ((...args: any[]) => void) | null {
    if (!this.isBrowser()) return null;
    const fbqFn = (window as any).fbq;
    return typeof fbqFn === 'function' ? fbqFn : null;
  }

 private sendEvent(
  eventName: string,
  customData: any = {},
  eventId?: string | null
  ): void {
    if (!this.isBrowser()) {
      return;
    }

    const effectiveEventId = eventId ?? this.generateEventId();
    const fbqFn = this.getFbq();

    if (fbqFn) {
      const pixelData: any = { ...(customData || {}) };
      const options: any = effectiveEventId ? { eventID: effectiveEventId } : undefined;

      if (this.userData) {
        if (this.userData.em) pixelData.em = this.userData.em;
        if (this.userData.ph) pixelData.ph = this.userData.ph;
        if (this.userData.fn) pixelData.fn = this.userData.fn;
        if (this.userData.ln) pixelData.ln = this.userData.ln;
      }

      fbqFn('track', eventName, pixelData, options);
    }

    if (!this.ENDPOINT) {
      return;
    }

    const user = this.authService.getLoggedInUser();
    if (user !== null) {
      this.setUser({
        email: user.username,
        phone: user.phone,
        firstName: user.firstName,
        lastName: user.lastName,
      });
    }

    const { fbp, fbc } = this.getMetaIds();

    const baseEvent: any = {
      event_name: eventName,
      event_time: Math.floor(Date.now() / 1000),
      event_id: effectiveEventId,
      action_source: 'website',
      event_source_url: window.location.href,
      user_data: this.userData || {},
      custom_data: customData || {},
    };

    if (fbp) {
      baseEvent.fbp = fbp;
    }

    if (fbc) {
      baseEvent.fbc = fbc;
    }

    const payload: any = {
      data: [baseEvent],
    };

    this.http.post(this.ENDPOINT, payload).subscribe({
      next: () => {},
      error: () => {},
    });
  }


  private generateEventId(): string {
    // Simple, fast, good enough for dedupe
    return (
      Date.now().toString(36) +
      Math.random().toString(36).slice(2, 10)
    );
  } 

  setUser(u: { email: string; phone?: string; firstName?: string; lastName?: string }) {
    // Solo actualiza el state local; se manda junto con cada evento
    this.userData = {};
    if (u.email) this.userData.em = u.email;
    if (u.phone) this.userData.ph = u.phone;
    if (u.firstName) this.userData.fn = u.firstName;
    if (u.lastName) this.userData.ln = u.lastName;
  }

  trackPageView(extra?: any, eventId?: string | null) {
    this.sendEvent('PageView', extra || {}, eventId);
  }

  bindSpaPageViews() {
    if (!this.isBrowser() || this.pageViewsBound) return;

    this.pageViewsBound = true;

    if (!this.initialPageViewTracked) {
      this.initialPageViewTracked = true;
      this.trackPageView({ page_path: this.router.url });
    }

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: any) => {
        this.trackPageView({ page_path: e.urlAfterRedirects });
      });
  }

  trackPurchase(customData: any, eventId?: string | null) {
    this.sendEvent('Purchase', customData || {}, eventId);
  }

  trackPaymentMode(customData: any, eventId?: string | null) {
    this.sendEvent('AddPaymentInfo', customData || {}, eventId);
  }

  trackCompleteRegistration(customData: any, eventId?: string | null) {
    this.sendEvent('CompleteRegistration', customData, eventId);
  }

  trackAddToCart(params: {
    raffleId: string;
    quantity: number;
    contextId?: string;
  }) {
    if (!this.isBrowser()) return;

    const baseId = params.contextId ?? params.raffleId;
    const eventId = `${baseId}_add_to_cart`;

    const purchaseCtx = {
      content_name: params.raffleId,
      currency: 'UYU',
      value: params.quantity
    };

    this.sendEvent('AddToCart', purchaseCtx, eventId);

    const event = {
      event_id: eventId,
      event: 'add_to_cart',
      category: 'purchase',
      action: 'add_to_cart',
      label: 'quantity',
      value: params.quantity
    };

    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push(event);
  }

  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()!.split(';').shift() || null;
    }
    return null;
  }

  private getFbp(): string | null {
    return this.getCookie('_fbp');
  }

  private getFbc(): string | null {
    const cookie = this.getCookie('_fbc');
    if (cookie) return cookie;

    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get('fbclid');
    if (fbclid) {
      return `fb.1.${Date.now()}.${fbclid}`;
    }

    return null;
  }

  private getMetaIds(): { fbp: string | null; fbc: string | null } {
    return {
      fbp: this.getFbp(),
      fbc: this.getFbc()
    };
  }
}
